/**
 * Conversion outbox health card.
 *
 * Backend shape:
 *   {
 *     pending: number,
 *     skipped_24h: number,
 *     sent_24h: number,
 *     oldest_pending_age_seconds: number | null,
 *   }
 *
 * Threshold alerting: oldest_pending > 5 minutes is the silent-DoS signal
 * the spec calls out (events.conversion_outbox stuck in pending). We
 * tint the card if we cross the threshold.
 */
import { formatNumber } from '@/lib/utils';
import { TileMeta, TileUnavailable, isUnavailable, tileShell } from './TileStatus';

interface Data extends TileMeta {
  pending: number;
  skipped_24h: number;
  sent_24h: number;
  oldest_pending_age_seconds: number | null;
}

const STALE_THRESHOLD_SECS = 5 * 60;

function fmtAge(secs: number | null): string {
  if (secs == null) return '—';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}min`;
  return `${(secs / 3600).toFixed(1)}h`;
}

export function OutboxCard({ data }: { data: Data }) {
  const unavailable = isUnavailable(data);
  const stale =
    !unavailable &&
    data?.oldest_pending_age_seconds != null &&
    data.oldest_pending_age_seconds > STALE_THRESHOLD_SECS;
  const n = (v: number) => (unavailable ? '—' : formatNumber(v));
  return (
    <section
      className={`border rounded-lg p-6 ${tileShell(
        unavailable,
        stale ? 'border-amber-500/50 bg-amber-500/5' : 'bg-card',
      )}`}
    >
      <h3 className="text-lg font-semibold mb-1">Conversion outbox</h3>
      <p className="text-xs text-muted-foreground mb-4">
        events.conversion_outbox dispatch pipeline.
      </p>
      {unavailable && <TileUnavailable error={data?.error} />}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Pending</div>
          <div className="text-2xl font-semibold">{n(data?.pending ?? 0)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Sent (24h)</div>
          <div className="text-2xl font-semibold">{n(data?.sent_24h ?? 0)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Skipped (24h)</div>
          <div className="text-2xl font-semibold">{n(data?.skipped_24h ?? 0)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">
            Oldest pending
          </div>
          <div
            className={`text-2xl font-semibold ${
              stale ? 'text-amber-700' : ''
            }`}
          >
            {unavailable ? '—' : fmtAge(data?.oldest_pending_age_seconds ?? null)}
          </div>
        </div>
      </div>
      {stale && (
        <p className="text-xs text-amber-700 mt-3">
          ⚠ Oldest pending is older than 5 minutes — dispatcher may be lagging.
        </p>
      )}
    </section>
  );
}
